// src/resources/commentReports/helpers.js

const uniqueBy = (items = [], selector) => {
    const map = new Map();
    items.forEach((item) => {
        const key = selector(item);
        if (!key || map.has(key)) {
            return;
        }
        map.set(key, item);
    });
    return Array.from(map.values());
};

export const getCommentAuthor = (record = {}) =>
    record.commentAuthor?.username ||
    record.comment?.author?.username ||
    record.commentAuthorUsername ||
    record.authorUsername ||
    record.author?.username ||
    'Auteur inconnu';

export const getCommentAuthorAvatar = (record = {}) =>
    record.commentAuthor?.avatarUrl ||
    record.comment?.author?.avatarUrl ||
    record.commentAuthorAvatar ||
    record.author?.avatarUrl ||
    null;

export const getCommentContent = (record = {}) =>
    record.commentContent ||
    record.comment?.content ||
    record.content ||
    record.message ||
    '';

export const getRdfMeta = (record = {}) => {
    if (record.rdf) {
        return {
            id: record.rdf.id || record.rdf.rdfId,
            slug: record.rdf.slug,
            title: record.rdf.title,
        };
    }
    return {
        id: record.rdfId || record.rdf?.id || null,
        slug: record.rdfSlug || null,
        title: record.rdfTitle || null,
    };
};

export const getReportCount = (record = {}) =>
    record.reportCount ??
    record.totalReports ??
    (Array.isArray(record.reports) ? record.reports.length : null) ??
    (Array.isArray(record.entries) ? record.entries.length : null) ??
    record.count ??
    0;

export const getReportEntries = (record = {}) => {
    if (Array.isArray(record.reports)) return record.reports;
    if (Array.isArray(record.entries)) return record.entries;
    if (Array.isArray(record.latestReports)) return record.latestReports;
    if (Array.isArray(record.history)) return record.history;

    if (record.lastReportReason || record.lastReporter) {
        return [
            {
                id: record.lastReportId || 'last-report',
                reason: record.lastReportReason,
                createdAt: record.lastReportAt || record.updatedAt,
                reporter: record.lastReporter || {
                    username: record.lastReporterUsername,
                    avatarUrl: record.lastReporterAvatar,
                },
            },
        ];
    }

    if (record.reason || record.reporter) {
        return [
            {
                id: record.id,
                reason: record.reason,
                createdAt: record.createdAt,
                reporter: record.reporter || {
                    username: record.reporterUsername,
                    avatarUrl: record.reporterAvatar,
                },
            },
        ];
    }
    return [];
};

export const getReasonStats = (record = {}) => {
    if (Array.isArray(record.reasonStats)) return record.reasonStats;
    if (Array.isArray(record.reasons)) return record.reasons;
    if (Array.isArray(record.reasonSummary)) return record.reasonSummary;
    const entries = getReportEntries(record);
    if (!entries.length) {
        if (record.reason) {
            return [{ reason: record.reason, count: getReportCount(record) || 1 }];
        }
        return [];
    }

    const tally = entries.reduce((acc, entry) => {
        const key = entry.reason || entry.label || 'Autre';
        acc[key] = (acc[key] || 0) + 1;
        return acc;
    }, {});

    return Object.entries(tally).map(([reason, count]) => ({ reason, count }));
};

export const getReporterList = (record = {}) => {
    if (Array.isArray(record.reporters) && record.reporters.length) {
        return record.reporters;
    }
    const entries = getReportEntries(record);
    const reporters = entries
        .map((entry) => entry.reporter || entry.user)
        .filter(Boolean);
    if (record.reporter) {
        reporters.push(record.reporter);
    }
    if (record.reporterUsername) {
        reporters.push({
            username: record.reporterUsername,
            avatarUrl: record.reporterAvatar,
        });
    }
    return uniqueBy(reporters, (user) => user?.username || user?.id);
};

export const truncate = (value, length = 180) => {
    if (!value) return '';
    if (value.length <= length) return value;
    return `${value.slice(0, length - 1)}…`;
};
